import { useState, useEffect, useRef } from "react";
import { Lock, X, ArrowRight } from "lucide-react";

const ACCENT = "#8b5cf6";
const ADMIN_PIN = "0154Randall!";
const LAUNCH_DATE = new Date("2026-11-14T08:00:00Z");
const FONT = "'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const EMAIL = "hello@lumenforge-mc.co.uk";

const YouTubeIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);
const PatreonIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M14.5 2C18.64 2 22 5.36 22 9.5S18.64 17 14.5 17 7 13.64 7 9.5 10.36 2 14.5 2M2 22V2h4v20H2z"/></svg>
);
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
);
const InstagramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
);

const SOCIALS = [
  { Icon: YouTubeIcon, url: "https://www.youtube.com/@Insid3_Th3_Cub3", label: "YouTube" },
  { Icon: PatreonIcon, url: "https://www.patreon.com/cw/InsideTheCube", label: "Patreon" },
  { Icon: TikTokIcon, url: "https://www.tiktok.com/@insid3_th3_cub3", label: "TikTok" },
  { Icon: InstagramIcon, url: "https://www.instagram.com/insid3th3cub3/", label: "Instagram" },
];

function getTimeLeft() {
  const diff = Math.max(0, LAUNCH_DATE - new Date());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

const HexMotif = () => (
  <svg aria-hidden="true" viewBox="0 0 200 200" style={{
    position: "absolute", left: "50%", top: "50%", width: "min(1100px, 140vmin)", height: "min(1100px, 140vmin)",
    transform: "translate(-50%, -50%)", pointerEvents: "none", animation: "mtSpin 240s linear infinite",
  }}>
    {[96, 78, 60, 42].map((r, i) => {
      const pts = Array.from({ length: 6 }, (_, k) => {
        const a = (Math.PI / 3) * k - Math.PI / 2;
        return `${100 + r * Math.cos(a)},${100 + r * Math.sin(a)}`;
      }).join(" ");
      return <polygon key={r} points={pts} fill="none" stroke="#fff" strokeOpacity={0.028 + i * 0.008} strokeWidth={i === 3 ? 7 : 5} strokeLinejoin="round" />;
    })}
  </svg>
);

const Unit = ({ value, label }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 54 }}>
    <span style={{ fontSize: "1.65rem", fontWeight: 600, color: "#fff", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", lineHeight: 1 }}>
      {String(value).padStart(2, "0")}
    </span>
    <span style={{ marginTop: 8, fontSize: 10, fontWeight: 500, color: "rgba(255,255,255,0.38)", letterSpacing: "0.22em", textTransform: "uppercase" }}>{label}</span>
  </div>
);

const Divider = () => <span aria-hidden="true" style={{ width: 1, height: 30, background: "rgba(255,255,255,0.1)" }} />;

export default function MaintenancePage({ onUnlock }) {
  const [t, setT] = useState(getTimeLeft());
  const [showPin, setShowPin] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    document.title = "Insid3 Th3 Cub3 · A new website is coming soon";
    const tick = setInterval(() => setT(getTimeLeft()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => { if (showPin && inputRef.current) inputRef.current.focus(); }, [showPin]);

  const submit = (e) => {
    e.preventDefault();
    if (pin.trim() === ADMIN_PIN) {
      sessionStorage.setItem("lf_maintenance_unlocked", "true");
      onUnlock();
    } else {
      setError(true);
      setPin("");
      setTimeout(() => setError(false), 1600);
    }
  };

  const hoverText = (on, off) => ({
    onMouseEnter: e => { e.currentTarget.style.color = on; },
    onMouseLeave: e => { e.currentTarget.style.color = off; },
  });

  return (
    <div data-testid="maintenance-page" style={{
      position: "fixed", inset: 0, overflow: "auto", background: "#0b0a12", color: "#fff", fontFamily: FONT,
      display: "flex", flexDirection: "column",
    }}>
      <div aria-hidden="true" style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
        <div style={{ position: "absolute", left: "50%", top: "46%", width: "70vmax", height: "70vmax", transform: "translate(-50%,-50%)",
          background: "radial-gradient(circle, rgba(139,92,246,0.13) 0%, rgba(139,92,246,0) 60%)" }} />
        <HexMotif />
      </div>

      <header style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "clamp(20px, 3vw, 40px) clamp(20px, 4vw, 56px)" }}>
        <div data-testid="maintenance-brand" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/brand/itc-logo.webp" alt="Insid3 Th3 Cub3" style={{ width: 40, height: 40, objectFit: "contain" }} />
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em" }}>Insid3 Th3 Cub3</div>
            <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(255,255,255,0.4)", letterSpacing: "0.02em" }}>Home of LumenForge-MC</div>
          </div>
        </div>
        <button data-testid="maintenance-pin-open" onClick={() => setShowPin(true)} aria-label="Admin access"
          style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer",
            color: "rgba(255,255,255,0.32)", fontFamily: FONT, fontSize: 12, fontWeight: 500, letterSpacing: "0.08em", transition: "color 0.25s ease" }}
          {...hoverText("#fff", "rgba(255,255,255,0.32)")}>
          <Lock size={13} /> Admin
        </button>
      </header>

      <main data-testid="maintenance-copy" style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "24px 20px 48px" }}>
        <div className="mt-rise" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28, animationDelay: "0.05s" }}>
          <span style={{ width: 34, height: 1.5, background: ACCENT }} />
          <span style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>Something new is on the way</span>
        </div>

        <h1 className="mt-rise" style={{ margin: 0, fontSize: "clamp(2.6rem, 7vw, 5.6rem)", fontWeight: 700, lineHeight: 1.02, letterSpacing: "-0.045em", animationDelay: "0.15s" }}>
          A new website is<br />coming{" "}
          <span style={{ fontStyle: "italic", fontWeight: 300, background: "linear-gradient(90deg, #a78bfa 0%, #60a5fa 60%, #2dd4bf 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", paddingRight: "0.08em" }}>soon.</span>
        </h1>

        <p className="mt-rise" style={{ margin: "28px 0 0", maxWidth: 520, fontSize: "clamp(0.98rem, 1.4vw, 1.12rem)", lineHeight: 1.65, color: "rgba(255,255,255,0.6)", animationDelay: "0.25s" }}>
          We're building a brand new home for Insid3 Th3 Cub3 and LumenForge-MC. In the meantime, we'd still love to hear from you.
        </p>

        <div className="mt-rise" data-testid="maintenance-countdown" style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 40, animationDelay: "0.35s" }}>
          <Unit value={t.days} label="Days" /><Divider />
          <Unit value={t.hours} label="Hours" /><Divider />
          <Unit value={t.minutes} label="Mins" /><Divider />
          <Unit value={t.seconds} label="Secs" />
        </div>
        <div data-testid="maintenance-launch-date" className="mt-rise" style={{ marginTop: 16, fontSize: 12, color: "rgba(255,255,255,0.35)", letterSpacing: "0.04em", animationDelay: "0.4s" }}>
          Launching Saturday 14 November 2026, 8am UK time
        </div>

        <a href={`mailto:${EMAIL}`} data-testid="maintenance-contact-button" className="mt-rise mt-cta" style={{ animationDelay: "0.5s" }}>
          Get in touch <ArrowRight size={16} />
        </a>
      </main>

      <footer style={{ position: "relative", zIndex: 2, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between", padding: "20px clamp(20px, 4vw, 56px) clamp(20px, 3vw, 36px)" }}>
        <a data-testid="maintenance-contact-email" href={`mailto:${EMAIL}`} style={{ fontSize: 13, color: "rgba(255,255,255,0.42)", textDecoration: "none", transition: "color 0.25s ease" }}
          {...hoverText("#fff", "rgba(255,255,255,0.42)")}>{EMAIL}</a>
        <div style={{ display: "flex", gap: 18 }}>
          {SOCIALS.map(({ Icon, url, label }) => (
            <a key={label} data-testid={`maintenance-social-${label.toLowerCase()}`} href={url} target="_blank" rel="noreferrer" aria-label={label}
              style={{ color: "rgba(255,255,255,0.42)", display: "inline-flex", transition: "color 0.25s ease" }}
              {...hoverText("#fff", "rgba(255,255,255,0.42)")}>
              <Icon size={17} />
            </a>
          ))}
        </div>
      </footer>

      {showPin && (
    <div
      data-testid="maintenance-pin-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(3,4,14,0.8)", backdropFilter: "blur(10px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={() => setShowPin(false)}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 360, padding: 28, borderRadius: 12,
          background: "rgba(14,13,22,0.96)", border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 30px 90px rgba(0,0,0,0.6)",
          animation: error ? "mtShake 0.4s ease" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
            Admin Access
          </div>
          <button
            type="button"
            data-testid="maintenance-pin-close"
            onClick={() => setShowPin(false)}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", cursor: "pointer", padding: 2, display: "inline-flex" }}
          >
            <X size={16} />
          </button>
        </div>
        <input
          ref={inputRef}
          data-testid="maintenance-pin-input"
          type="password"
          autoComplete="off"
          value={pin}
          onChange={e => setPin(e.target.value)}
          placeholder="Enter password"
          style={{
            width: "100%", padding: "14px 16px", borderRadius: 6,
            background: "rgba(255,255,255,0.05)",
            border: `1px solid ${error ? "#ef4444" : "rgba(255,255,255,0.14)"}`,
            color: "#fff", fontSize: "1rem", letterSpacing: "0.12em",
            textAlign: "center", fontFamily: "inherit", outline: "none",
            transition: "border-color 0.25s ease",
          }}
        />
        {error && (
          <div data-testid="maintenance-pin-error" style={{ marginTop: 10, fontSize: 12, color: "#f87171", textAlign: "center" }}>
            Incorrect password
          </div>
        )}
        <button
          type="submit"
          data-testid="maintenance-pin-submit"
          style={{
            marginTop: 18, width: "100%", padding: "13px 18px", borderRadius: 6,
            background: ACCENT, color: "#fff", border: `1px solid ${ACCENT}`,
            fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
            cursor: "pointer", fontFamily: "inherit",
            boxShadow: `0 10px 26px ${ACCENT}44`,
          }}
        >
          Unlock Site
        </button>
      </form>
    </div>
  )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter+Tight:ital,wght@0,300..800;1,300..800&display=swap');
        @keyframes mtSpin { to { transform: translate(-50%, -50%) rotate(360deg); } }
        @keyframes mtRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes mtShake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-7px); } 75% { transform: translateX(7px); } }
        .mt-rise { opacity: 0; animation: mtRise 0.9s cubic-bezier(.2,.7,.2,1) forwards; }
        .mt-cta { margin-top: 40px; display: inline-flex; align-items: center; gap: 10px; padding: 14px 26px; border-radius: 999px;
          background: #fff; color: #0b0a12; font-weight: 600; font-size: 15px; text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease; }
        .mt-cta:hover { background: ${ACCENT}; color: #fff; transform: translateY(-2px); }
      `}</style>
    </div>
  );
}
